import { useMutation, useQuery, UseQueryOptions, useQueryClient, UseMutationOptions } from '@tanstack/react-query';
import { CustomResponseEntity, PaginationParams } from '../types/common';
import {
  CreateFarmLocationDto,
  CreateOrganizationDto,
  CreateOrganizationUserDto,
  FarmLocationDto,
  OrganizationDto,
  OrganizationUserDto,
  UpdateFarmLocationDto,
  UpdateOrganizationDto,
  UpdateOrganizationUserDto,
  OrganizationSearchParams,
  OrganizationSearchResponse,
} from '../types/dtos';
import {
  organizationService,
  organizationUserService,
  FarmLocationService,
} from '../services/organization.service';

// Query keys
export const organizationKeys = {
  all: ['organization'] as const,
  organizations: () => [...organizationKeys.all, 'list'] as const,
  organization: (id: string) => [...organizationKeys.organizations(), id] as const,
  organizationFarms: () => [...organizationKeys.all, 'farms'] as const,
  organizationUsers: () => [...organizationKeys.all, 'users'] as const,
  organizationUser: (id: string) => [...organizationKeys.organizationUsers(), id] as const,
  farmLocations: () => [...organizationKeys.all, 'farm-locations'] as const,
  farmLocation: (id: string) => [...organizationKeys.farmLocations(), id] as const,
  search: (params: OrganizationSearchParams) => [...organizationKeys.all, 'search', params] as const,
};

// Organizations
export const useOrganizations = (
  params?: PaginationParams,
  options?: UseQueryOptions<CustomResponseEntity<OrganizationDto[]>>
) => {
  return useQuery({
    queryKey: organizationKeys.organizations(),
    queryFn: () => organizationService.findAll(params),
    ...options,
  });
};

export const useOrganization = (id: string, options?: UseQueryOptions<CustomResponseEntity<OrganizationDto>>) => {
  return useQuery({
    queryKey: organizationKeys.organization(id),
    queryFn: () => organizationService.findById(id),
    ...options,
  });
};

export const useOrganizationFarms = (options?: UseQueryOptions<CustomResponseEntity<FarmLocationDto[]>>) => {
  return useQuery({
    queryKey: organizationKeys.organizationFarms(),
    queryFn: () => organizationService.findAllFarms(),
    ...options,
  });
};

export const useCreateOrganization = (
  options?: UseMutationOptions<
    CustomResponseEntity<OrganizationDto>,
    Error,
    CreateOrganizationDto,
    { organizationId?: string }
  >
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationDto) => organizationService.create(data),
    onSuccess: (response, variables, context) => {
      // Extract the organization ID from the response
      const organizationId = response.data?.id;
      
      if (organizationId) {
        // Invalidate and refetch organizations list
        queryClient.invalidateQueries({
          queryKey: organizationKeys.organizations(),
        });
        
        // Invalidate and refetch main organizations
        queryClient.invalidateQueries({
          queryKey: organizationKeys.organizations(),
        });
        
        // Call the custom onSuccess callback if provided
        if (options?.onSuccess) {
          options.onSuccess(response, variables, { ...context, organizationId });
        }
      }
    },
    ...options,
  });
};

export const useUpdateOrganization = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationDto }) =>
      organizationService.update(id, data),
  });
};

export const useDeleteOrganization = () => {
  return useMutation({
    mutationFn: (id: string) => organizationService.delete(id),
  });
};

// Organization Users
export const useOrganizationUsers = (
  params?: PaginationParams,
  options?: UseQueryOptions<CustomResponseEntity<OrganizationUserDto[]>>
) => {
  return useQuery({
    queryKey: organizationKeys.organizationUsers(),
    queryFn: () => organizationUserService.findAll(params),
    ...options,
  });
};

export const useOrganizationUser = (id: string, options?: UseQueryOptions<CustomResponseEntity<OrganizationUserDto>>) => {
  return useQuery({
    queryKey: organizationKeys.organizationUser(id),
    queryFn: () => organizationUserService.findById(id),
    ...options,
  });
};

export const useCreateOrganizationUser = () => {
  return useMutation({
    mutationFn: (data: CreateOrganizationUserDto) => organizationUserService.create(data),
  });
};

export const useUpdateOrganizationUser = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationUserDto }) =>
      organizationUserService.update(id, data),
  });
};

export const useDeleteOrganizationUser = () => {
  return useMutation({
    mutationFn: (id: string) => organizationUserService.delete(id),
  });
};

// Farm Locations
export const useFarmLocations = (
  organizationId: string,
  params?: PaginationParams,
  options?: UseQueryOptions<CustomResponseEntity<FarmLocationDto[]>>
) => {
  const farmLocationService = new FarmLocationService(organizationId);
  return useQuery({
    queryKey: organizationKeys.farmLocations(),
    queryFn: () => farmLocationService.findAll(params),
    ...options,
  });
};

export const useFarmLocation = (
  organizationId: string,
  id: string,
  options?: UseQueryOptions<CustomResponseEntity<FarmLocationDto>>
) => {
  const farmLocationService = new FarmLocationService(organizationId);
  return useQuery({
    queryKey: organizationKeys.farmLocation(id),
    queryFn: () => farmLocationService.findById(id),
    ...options,
  });
};

export const useCreateFarmLocation = (organizationId: string) => {
  const farmLocationService = new FarmLocationService(organizationId);
  return useMutation({
    mutationFn: (data: CreateFarmLocationDto) => farmLocationService.create(data),
  });
};

export const useUpdateFarmLocation = (organizationId: string) => {
  const farmLocationService = new FarmLocationService(organizationId);
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFarmLocationDto }) =>
      farmLocationService.update(id, data),
  });
};

export const useDeleteFarmLocation = (organizationId: string) => {
  const farmLocationService = new FarmLocationService(organizationId);
  return useMutation({
    mutationFn: (id: string) => farmLocationService.delete(id),
  });
};

// Search Organizations
export const useSearchOrganizations = (
  params: OrganizationSearchParams,
  options?: UseQueryOptions<CustomResponseEntity<OrganizationSearchResponse>>
) => {
  return useQuery({
    queryKey: organizationKeys.search(params),
    queryFn: () => organizationService.searchChildOrganizations(params),
    ...options,
  });
}; 